import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { ModuleNode, DemoPanel, ModuleMaturity } from '../types/showcase';
import { isRecentlyChanged } from '../data/recent-changes';
import { useDownloadStats } from '../hooks/useDownloadStats';
import moduleMaturityJson from '../data/module-maturity.json';

// queue #658 §R658-2③: 트리 '갱신' 배지 — §R643 산출물 read-only 재사용(신규 데이터 원천 0).
const MODULE_MATURITY = moduleMaturityJson as unknown as ModuleMaturity;


/** Map from module code to demo panel */
function getDemoPanel(code: string, demoEndpoint: string | null): DemoPanel {
  // R11-#293: C009 파일관리 — redirect to info panel; actual demo is in /attachments menu
  if (code === 'C009') return 'info';
  if (!demoEndpoint) return 'info';
  if (demoEndpoint.includes('/validate')) return 'validate';
  if (demoEndpoint.includes('/mask')) return 'mask';
  if (demoEndpoint.includes('/excel')) return 'excel';
  if (demoEndpoint.includes('/event')) return 'event';
  /* R11-#308: '/error' WAF 차단 → '/exception-demo' rename. 옛 path 도 호환 유지. */
  if (demoEndpoint.includes('/exception-demo') || demoEndpoint.includes('/error')) return 'error';
  if (demoEndpoint.includes('/file')) return 'file';
  if (demoEndpoint.includes('/util/')) return 'utility';
  if (demoEndpoint.includes('/crypto/')) return 'crypto';
  if (demoEndpoint.includes('/jpa/')) return 'jpa';
  // R11-#292: C007 캐시 설정 → cache demo (BE catalog now sets demoEndpoint=SHOWCASE_CACHE)
  if (demoEndpoint.includes('/cache')) return 'cache';
  // R12 #329-#333: gateway demo panels
  if (demoEndpoint.includes('SHOWCASE_ROUTE_MATCH') || demoEndpoint.includes('/route-match')) return 'routeMatch';
  if (demoEndpoint.includes('SHOWCASE_AUTH_CHECK') || demoEndpoint.includes('/auth-check')) return 'authCheck';
  if (demoEndpoint.includes('SHOWCASE_RATE_LIMIT') || demoEndpoint.includes('/rate-limit')) return 'rateLimit';
  if (demoEndpoint.includes('SHOWCASE_HEALTH_CHECK') || demoEndpoint.includes('/health-check-demo')) return 'healthCheck';
  // C055 Kafka 메시징 라이브 데모
  if (demoEndpoint.includes('/demo/kafka')) return 'kafka';
  // C057 RabbitMQ 메시징 라이브 데모
  if (demoEndpoint.includes('/demo/rabbitmq')) return 'rabbitmq';
  // C053 Elasticsearch 전문 검색 라이브 데모
  if (demoEndpoint.includes('/demo/elasticsearch')) return 'elasticsearch';
  // C054 MongoDB 문서 저장 라이브 데모
  if (demoEndpoint.includes('/demo/mongo')) return 'mongo';
  // C072 OpenTelemetry 관측 status 라이브 데모
  if (demoEndpoint.includes('/demo/otel')) return 'otel';
  // C056 Outbox 패턴 라이브 데모 (recorded→published→consumed 단계 진행)
  if (demoEndpoint.includes('/demo/outbox')) return 'outbox';
  // C035 OAuth2/OIDC 서버간 브리지 라이브 데모 (#605)
  if (demoEndpoint.includes('/demo/oauth2')) return 'oauth2';
  // queue #646 §R646-4: 미데모 10종 신규 데모 패널(CommonShowcasePage.tsx resolveDemoPanel 과 동일 로직)
  if (demoEndpoint.includes('/demo/state-machine')) return 'stateMachine';
  if (demoEndpoint.includes('/demo/saga')) return 'saga';
  if (demoEndpoint.includes('/demo/report')) return 'report';
  if (demoEndpoint.includes('/demo/retention')) return 'retention';
  if (demoEndpoint.includes('/demo/config')) return 'config';
  if (demoEndpoint.includes('/demo/barcode')) return 'barcode';
  if (demoEndpoint.includes('/demo/template')) return 'template';
  if (demoEndpoint.includes('/demo/pdf')) return 'pdf';
  if (demoEndpoint.includes('/demo/image')) return 'image';
  if (demoEndpoint.includes('/demo/feature-toggle')) return 'featureToggle';
  return 'info';
}

interface Props {
  nodes: ModuleNode[];
  selectedCode: string | null;
  onSelect: (node: ModuleNode, panel: DemoPanel) => void;
  searchQuery?: string;
  /**
   * queue #658 §R658-2③ — '갱신' 배지 판정 기준 시각. 렌더 순수성을 위해 상위(페이지)에서
   * 1회 계산해 주입한다(★일자 시한폭탄 금지 — 컴포넌트 내부 `new Date()` 호출 금지).
   * 미전달 시 배지를 렌더하지 않는다(옵셔널 — 기존 호출부 회귀 방지).
   */
  now?: Date;
}

/**
 * queue#573: eraf-gateway 그룹은 두 개의 서로 다른 G-넘버링 체계를 함께 담는다 —
 * G001~G015 (쇼케이스 자체 데모 체계, R12) 와 G016~G060 (common-capabilities.md 로드맵
 * 체계, 신규). 코드 숫자만으로 구분되므로(둘 다 "eraf-gateway" artifact) 트리에서
 * 로드맵 노드 앞에 서브그룹 라벨을 끼워 넣어 두 체계가 다름을 시각적으로 분리한다.
 */
const GATEWAY_ROADMAP_CODE_THRESHOLD = 16;
function isGatewayRoadmapNode(node: ModuleNode): boolean {
  const n = Number.parseInt(node.code.replace(/\D/g, ''), 10);
  return node.artifact === 'eraf-gateway' && Number.isFinite(n) && n >= GATEWAY_ROADMAP_CODE_THRESHOLD;
}
const GATEWAY_ROADMAP_SUBGROUP_LABEL = {
  ko: 'Gateway 능력 (로드맵 G016~G060)',
  en: 'Gateway Capabilities (Roadmap G016~G060)',
};

export function ModuleCatalogTree({ nodes, selectedCode, onSelect, searchQuery = '', now }: Props) {
  const { t, i18n } = useTranslation();
  // UAT 2026-05-20 #459: EN 토글 시 모듈명도 영문으로 표시 (i18n 미초기화 시 ko 기본)
  const isEn = (i18n?.language ?? 'ko').toLowerCase().startsWith('en');
  /* R11-#292: 기존 합성 CACHE pseudo-node 주입 제거 — BE 카탈로그의 C007 이 실제 cache demo 를 제공. */
  const allNodes = nodes;

  // queue #662 §R662-3: 다운로드 수 + '인기' 배지 — 부가 지표라 로딩/실패 시 조용히 미표시
  // (fail-soft). 트리·카드가 동일 쿼리키(['download-stats'])를 공유해 TanStack Query 가 dedupe.
  const { data: downloadStats } = useDownloadStats();
  const downloadCountByCode = useMemo(() => {
    const map = new Map<string, number>();
    downloadStats?.byModule.forEach((m) => map.set(m.code, m.count));
    return map;
  }, [downloadStats]);
  const popularCodes = useMemo(
    () => new Set(downloadStats?.topModules.map((m) => m.code) ?? []),
    [downloadStats]
  );

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return allNodes;
    const q = searchQuery.toLowerCase();
    return allNodes.filter(
      (n) =>
        n.code.toLowerCase().includes(q) ||
        n.nameKo.toLowerCase().includes(q) ||
        n.nameEn.toLowerCase().includes(q) ||
        n.artifact.toLowerCase().includes(q)
    );
  }, [allNodes, searchQuery]);

  const artifactNames = useMemo(() => {
    const order = [
      'eraf-core',
      'eraf-data-jpa',
      'eraf-security',
      'eraf-swagger',
      'eraf-actuator',
      'eraf-excel',
    ];
    const seen = new Set(order);
    filtered.forEach((n) => {
      if (!seen.has(n.artifact)) {
        seen.add(n.artifact);
        order.push(n.artifact);
      }
    });
    return order.filter((a) => filtered.some((n) => n.artifact === a));
  }, [filtered]);

  // R2-005: 모든 아티팩트 기본 펼침 (사용자가 전체 26 모듈을 한눈에 볼 수 있도록)
  // R7-088 fix: useState initializer runs before data loads (nodes=[]) → empty Set.
  // useEffect syncs expandedArtifacts whenever new artifact names appear after API response.
  const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(
    () => new Set(artifactNames)
  );

  useEffect(() => {
    if (artifactNames.length > 0) {
      setExpandedArtifacts((prev) => {
        // Only auto-expand if nothing has been manually expanded yet (first data arrival)
        if (prev.size === 0) {
          return new Set(artifactNames);
        }
        // Also expand any newly added artifact groups
        const next = new Set(prev);
        let changed = false;
        artifactNames.forEach((a) => {
          if (!next.has(a)) {
            next.add(a);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [artifactNames]);

  function toggleArtifact(artifact: string) {
    setExpandedArtifacts((prev) => {
      const next = new Set(prev);
      if (next.has(artifact)) {
        next.delete(artifact);
      } else {
        next.add(artifact);
      }
      return next;
    });
  }

  function expandAll() {
    setExpandedArtifacts(new Set(artifactNames));
  }

  function collapseAll() {
    setExpandedArtifacts(new Set());
  }

  function handleNodeClick(node: ModuleNode) {
    const panel = getDemoPanel(node.code, node.demoEndpoint);
    onSelect(node, panel);
  }

  function handleNodeKeyDown(e: React.KeyboardEvent, node: ModuleNode) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNodeClick(node);
    }
  }

  const allExpanded = expandedArtifacts.size === artifactNames.length;

  return (
    <>
      {/* R2-005: 모두 펼침/닫힘 토글 */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-3)',
          borderBottom: '1px solid var(--color-surface-border)',
        }}
      >
        <button
          type="button"
          onClick={allExpanded ? collapseAll : expandAll}
          style={{
            background: 'none',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
          aria-label={allExpanded ? t('showcase.catalog.tree.collapseAllAria') : t('showcase.catalog.tree.expandAllAria')}
        >
          {allExpanded ? t('showcase.catalog.tree.collapseAll') : t('showcase.catalog.tree.expandAll')}
        </button>
      </div>
      <ul
        role="tree"
        aria-label={t('showcase.catalog.tree.ariaLabel')}
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
        }}
      >
        {artifactNames.map((artifact) => {
        const groupNodes = filtered.filter((n) => n.artifact === artifact);
        const isExpanded = expandedArtifacts.has(artifact);

        // queue #658 §R658-2③: artifact 단위 최근 변경 여부(최근 14일) — 그룹 aria-label(count) 는 불변.
        const isRecent = !!now && isRecentlyChanged(MODULE_MATURITY.modules?.[artifact]?.lastChanged ?? null, now);

        return (
          <li
            key={artifact}
            role="treeitem"
            aria-expanded={isExpanded}
            aria-label={`${artifact} (${groupNodes.length})`}
          >
            {/* Artifact group header */}
            <button
              type="button"
              onClick={() => toggleArtifact(artifact)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                width: '100%',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                border: 'none',
                borderBottom: '1px solid var(--color-surface-border)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                color: 'var(--color-text-secondary)',
                fontFamily: 'var(--font-family-sans)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textAlign: 'left',
              }}
              aria-expanded={isExpanded}
            >
              <span
                aria-hidden="true"
                style={{
                  fontSize: 10,
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  display: 'inline-block',
                  width: 12,
                }}
              >
                ▶
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  color: 'var(--color-brand-primary)',
                  flex: 1,
                }}
              >
                {artifact}
              </span>
              <span
                style={{
                  background: 'var(--color-surface-muted)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-full)',
                  padding: '1px 6px',
                  fontSize: 10,
                  color: 'var(--color-text-muted)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                {groupNodes.length}
              </span>
              {/* queue #658 §R658-2③: '갱신' 배지 — 별도 data-recent-badge/aria-label 로 그룹 aria-label(count) 오염 방지 */}
              {isRecent && (
                <span data-recent-badge aria-label={t('showcase.recentChanges.updatedBadgeAria', { artifact })}>
                  <StatusPill tone="info">{t('showcase.recentChanges.updatedBadge')}</StatusPill>
                </span>
              )}
            </button>

            {/* Module nodes */}
            {isExpanded && (
              <ul role="group" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(() => {
                  const firstRoadmapCode = groupNodes.find(isGatewayRoadmapNode)?.code;
                  return groupNodes.map((node) => {
                  const isSelected = selectedCode === node.code;
                  // R11-#293: C009 routes to 'info', not file demo
                  const hasDemo = !!node.demoEndpoint && node.code !== 'C009';
                  // queue#573: subgroup separator right before the first G016~G060 roadmap node
                  const showRoadmapSubgroupHeader = node.code === firstRoadmapCode;
                  // #617: SUPERSEDED 노드는 흐린 스타일 + 배지로 "대체됨" 을 시각적으로 구분.
                  // null 은 JSON 상 생략될 수 있어 `!= null` 로 null/undefined 를 함께 처리.
                  const isSuperseded = node.supersededBy != null;

                  return (
                    <>
                    {showRoadmapSubgroupHeader && (
                      <li
                        key={`${artifact}-roadmap-subgroup`}
                        role="separator"
                        aria-label={isEn ? GATEWAY_ROADMAP_SUBGROUP_LABEL.en : GATEWAY_ROADMAP_SUBGROUP_LABEL.ko}
                        style={{
                          padding: 'var(--spacing-1) var(--spacing-4)',
                          fontSize: 9,
                          fontWeight: 700,
                          color: 'var(--color-text-muted)',
                          background: 'var(--color-surface-muted)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {isEn ? GATEWAY_ROADMAP_SUBGROUP_LABEL.en : GATEWAY_ROADMAP_SUBGROUP_LABEL.ko}
                      </li>
                    )}
                    <li
                      key={node.code}
                      role="treeitem"
                      aria-selected={isSelected}
                      aria-label={`${node.code} ${node.nameKo}`}
                    >
                      <button
                        type="button"
                        onClick={() => handleNodeClick(node)}
                        onKeyDown={(e) => handleNodeKeyDown(e, node)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-2)',
                          width: '100%',
                          padding: 'var(--spacing-2) var(--spacing-4)',
                          background: isSelected
                            ? 'var(--color-sidebar-active-bg)'
                            : 'transparent',
                          border: 'none',
                          borderLeft: `2px solid ${
                            isSelected ? 'var(--color-brand-primary)' : 'transparent'
                          }`,
                          borderBottom: '1px solid var(--color-surface-border)',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          fontFamily: 'var(--font-family-sans)',
                          textAlign: 'left',
                          color: isSelected
                            ? 'var(--color-brand-primary)'
                            : 'var(--color-text-primary)',
                          opacity: isSuperseded ? 0.55 : 1,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-xs)',
                            color: isSelected
                              ? 'var(--color-brand-primary)'
                              : 'var(--color-text-muted)',
                            minWidth: 36,
                          }}
                        >
                          {node.code}
                        </span>
                        <span style={{ flex: 1, fontSize: 'var(--font-size-xs)', fontStyle: isSuperseded ? 'italic' : 'normal' }}>
                          {isEn && node.nameEn?.trim() ? node.nameEn : node.nameKo}
                        </span>
                        {isSuperseded && (
                          <span
                            aria-label={t('showcase.catalog.tree.supersededTag')}
                            style={{
                              display: 'inline-block',
                              padding: '0 var(--spacing-1)',
                              fontSize: 9,
                              fontWeight: 700,
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--color-warning)',
                              color: 'var(--color-warning)',
                              letterSpacing: '0.04em',
                              lineHeight: 1.6,
                              flexShrink: 0,
                            }}
                          >
                            {t('showcase.catalog.tree.supersededTag')}
                          </span>
                        )}
                        {hasDemo && (
                          <span
                            aria-label={t('showcase.catalog.tree.demoTag')}
                            style={{
                              display: 'inline-block',
                              padding: '0 var(--spacing-1)',
                              fontSize: 9,
                              fontWeight: 700,
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--color-brand-primary)',
                              color: 'var(--color-brand-primary)',
                              letterSpacing: '0.04em',
                              lineHeight: 1.6,
                              flexShrink: 0,
                            }}
                          >
                            DEMO
                          </span>
                        )}
                        {/*
                          queue #662 §R662-3: 다운로드 수 + '인기' 배지 — 전용 data 속성 + aria-label 로
                          기존 노드 aria-label(`${code} ${nameKo}`)·DEMO/SUPERSEDED 태그와 분리해
                          e2e 셀렉터 부분매치 오염을 차단한다(#658 트리 배지 선례). downloadStats
                          미로딩/실패 시 조용히 미표시(fail-soft).
                        */}
                        {downloadCountByCode.has(node.code) && (
                          <span
                            data-download-count
                            aria-label={t('showcase.download.count', {
                              count: downloadCountByCode.get(node.code) ?? 0,
                            })}
                            style={{
                              fontSize: 9,
                              color: 'var(--color-text-muted)',
                              fontFamily: 'var(--font-family-mono)',
                              flexShrink: 0,
                            }}
                          >
                            {downloadCountByCode.get(node.code)}
                          </span>
                        )}
                        {popularCodes.has(node.code) && (
                          <span
                            data-popular-badge
                            aria-label={t('showcase.download.popularBadgeAria', { code: node.code })}
                            style={{ flexShrink: 0 }}
                          >
                            <StatusPill tone="info">{t('showcase.download.popularBadge')}</StatusPill>
                          </span>
                        )}
                      </button>
                    </li>
                    </>
                  );
                  });
                })()}
              </ul>
            )}
          </li>
        );
      })}
      </ul>
    </>
  );
}
